"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Circles() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const footer = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const films = [{
    title: "Cercle Postnatal",
    director: "Genève",
    year: "2024",
    restoration: "BIEN-ÊTRE",
    runtime: "90 MIN",
    synopsis: "Un espace de parole bienveillant pour partager les joies et les défis de la maternité dans un cadre apaisant.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/cercle_postnatal.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/cercle_postnatal.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/cercle_postnatal.webp-wvc-sizes",
    membersOnly: true
  }, {
    title: "Atelier Nutrition",
    director: "Nyon",
    year: "2024",
    restoration: "CUISINE",
    runtime: "120 MIN",
    synopsis: "Apprenez à préparer des repas sains et nourrissants pour votre période de post-partum avec des produits locaux.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/atelier_nutrition.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/atelier_nutrition.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/atelier_nutrition.webp-wvc-sizes",
    membersOnly: false
  }, {
    title: "Soin Rebozo",
    director: "La Côte",
    year: "2024",
    restoration: "DÉTENTE",
    runtime: "180 MIN",
    synopsis: "Un rituel de passage ancestral pour honorer votre corps et célébrer votre transformation en tant que mère.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soin_rebozo.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soin_rebozo.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/soin_rebozo.webp-wvc-sizes",
    membersOnly: true
  }, {
    title: "Village Maman",
    director: "Genève",
    year: "2024",
    restoration: "COMMUNAUTÉ",
    runtime: "60 MIN",
    synopsis: "Rejoignez notre communauté pour échanger des conseils, trouver du soutien et briser l'isolement du post-partum.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_maman.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_maman.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1719083/img/village_maman.png-wvc-sizes",
    membersOnly: false
  }];
  return;
}
type FilmCardProps = {
  film: {
    title: string;
    director: string;
    year: string;
    restoration: string;
    runtime: string;
    synopsis: string;
    image: string;
    srcset: string;
    sizes: string;
    membersOnly: boolean;
  };
  offsetClass?: string;
  "data-index"?: number;
};
function FilmCard({
  film,
  offsetClass = "",
  ...rest
}: FilmCardProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={rest["data-index"]} className={`group ${offsetClass} transition-all duration-[1100ms] ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      {}
      <div className="relative overflow-hidden rounded-lg">
        <div className="relative aspect-[2/3] overflow-hidden bg-muted rounded-lg">
          <img src={film.image} data-wvc-srcset={film.srcset} data-wvc-sizes={film.sizes} alt={`${film.title} illustration`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-all duration-[800ms] ease-out group-hover:scale-[1.04]" />

          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.18] mix-blend-overlay" style={{
          backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23g)'/></svg>\")"
        }} />

          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
          background: "radial-gradient(ellipse at center, transparent 50%, hsla(20, 10%, 4%, 0.3) 100%)"
        }} />

          {}
          <div aria-hidden="true" className="pointer-events-none absolute inset-3 border border-primary/0 transition-all duration-700 group-hover:inset-2 group-hover:border-primary/70" />

          {}
          {film.membersOnly && <div className="absolute right-4 top-4 z-10">
              <div className="bg-primary px-2.5 py-1 font-default text-[9px] uppercase tracking-[0.25em] text-white rounded-md">
                Places limitées
              </div>
            </div>}
        </div>
      </div>

      {}
      <div className="mt-6 font-default text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {film.year} <span className="mx-1.5 text-primary/60">·</span>{" "}
        {film.restoration}{" "}
        <span className="mx-1.5 text-primary/60">·</span> {film.runtime}
      </div>

      {}
      <h3 className="font-default mt-4 text-3xl leading-[1.1] tracking-tight md:text-4xl">
        {film.title}
      </h3>

      {}
      <p className="font-default mt-2 text-lg text-primary/90">
        Lieu : {film.director}
      </p>

      {}
      <p className="font-default mt-4 max-w-md text-sm leading-relaxed text-muted-foreground">
        {film.synopsis}
      </p>

      {}
      <div className="mt-6">
        <Button asChild variant="outline" className="h-11 border-primary/40 bg-transparent px-6 font-default text-[10px] uppercase tracking-[0.3em] text-foreground hover:bg-primary hover:text-white hover:border-primary rounded-lg">
          <Link to="/ContactPage">
            Réserver ma place
            <ArrowRight className="ml-1 size-3.5" />
          </Link>
        </Button>
      </div>
    </div>;
}